/**
 * CLIENT mirror of the organization RBAC (`auth/org_rbac.rs`). UX only:
 * decides which edit controls to SHOW. The real authority is the write-path
 * (`db/sync.rs`), which rejects-and-logs what's unauthorized — the client is
 * never the authority. Keep in parity with Rust's capability map.
 */

/** Capabilities of the organization plane (subset used by the UI). */
export type OrgCapability =
  | "ManageOrgSettings"
  | "ManageCampuses"
  | "ManageMembers"
  | "ManageAcademics"
  | "ManageStudents"
  | "ViewCampusData";

/**
 * ALL capabilities of the organization plane, in display order.
 *
 * SINGLE source for any screen that EXPLAINS what a role allows
 * (§M1 "My profiles"): walking this list and asking [`orgRoleGrants`] is
 * what stops the explanation from drifting from enforcement. The `satisfies`
 * forces exhaustiveness at compile time — adding an `OrgCapability` without
 * adding it here doesn't compile.
 */
export const ORG_CAPABILITIES = [
  "ManageOrgSettings",
  "ManageCampuses",
  "ManageMembers",
  "ManageAcademics",
  "ManageStudents",
  "ViewCampusData",
] as const satisfies readonly OrgCapability[];

/** Exhaustiveness check: every `OrgCapability` is in the list. */
type _AllCapabilitiesListed =
  Exclude<OrgCapability, (typeof ORG_CAPABILITIES)[number]> extends never
    ? true
    : ["falta una OrgCapability en ORG_CAPABILITIES"];
const _capabilitiesAreExhaustive: _AllCapabilitiesListed = true;
void _capabilitiesAreExhaustive;

/** All canonical organization roles, from highest to lowest privilege. */
export const ORG_ROLES = [
  "owner",
  "org_admin",
  "campus_admin",
  "staff",
  "teacher",
  "guardian",
  "student",
] as const satisfies readonly OrgRole[];

/** Canonical role of `member.role` (with legacy alias `admin` → org_admin). */
export type OrgRole =
  | "owner"
  | "org_admin"
  | "campus_admin"
  | "teacher"
  | "staff"
  | "student"
  | "guardian";

/** Normalizes a raw `member.role` to the canonical role (unknown → student). */
export function normalizeOrgRole(role: string | null | undefined): OrgRole {
  switch (role) {
    case "owner": {
      return "owner";
    }
    case "org_admin":
    case "admin": {
      return "org_admin";
    }
    case "campus_admin": {
      return "campus_admin";
    }
    case "teacher": {
      return "teacher";
    }
    case "staff": {
      return "staff";
    }
    case "guardian": {
      return "guardian";
    }
    default: {
      return "student";
    }
  }
}

/** Does the role grant the capability? Parity with `org_role_grants` (Rust). */
export function orgRoleGrants(cap: OrgCapability, role: OrgRole): boolean {
  switch (role) {
    case "owner":
    case "org_admin": {
      return true;
    }
    case "campus_admin": {
      return (
        cap === "ManageCampuses" ||
        cap === "ManageStudents" ||
        cap === "ManageAcademics" ||
        cap === "ViewCampusData"
      );
    }
    case "staff": {
      return cap === "ManageStudents" || cap === "ViewCampusData";
    }
    case "teacher": {
      return cap === "ViewCampusData";
    }
    default: {
      return false;
    }
  }
}

/** Convenience: does the role (text) grant the capability? */
export function roleHasCapability(role: string | null | undefined, cap: OrgCapability): boolean {
  return orgRoleGrants(cap, normalizeOrgRole(role));
}

/**
 * D3 · can the role see other organization members' EMAIL? Mirror of
 * `org_rbac::can_view_member_emails` (Rust). **UX only** — who sees the email
 * is decided by the server when building the `listMembers` response; here it
 * just avoids rendering an "Email" column that would always arrive empty.
 */
export function canViewMemberEmails(role: string | null | undefined): boolean {
  return roleHasCapability(role, "ManageMembers") || roleHasCapability(role, "ViewCampusData");
}
